import { Box, useTheme } from '@mui/material';
import { Map as OLMap } from 'ol';
import { Coordinate } from 'ol/coordinate';
import 'ol/ol.css';
import React, { FC, PropsWithChildren, RefObject, useEffect } from 'react';
import FontAwesome from 'react-fontawesome';
import { NavigateFunction } from 'react-router';

import ErrorButtonWithFeedback from '../../Error/ErrorButtonWithFeedback';
import { LoadingCardContent } from '../../Loading';
import useMap, { UseMapProps } from '../hooks/useMap';
import useOverlay from '../hooks/useOverlay';
import { MapVariant } from '../lib/types';
import OverlayContainer from '../OverlayContainer';

import './geocoder.css';

export interface MapCanvasProps extends UseMapProps {
  navigate?: NavigateFunction;
  overlay?: JSX.Element;
  overlayPosition?: Coordinate;
  setMap?: (map: OLMap) => void;
  variant?: MapVariant;
}

const Wrapper: FC<PropsWithChildren<{ variant?: MapVariant }>> = ({ children, variant }) => {
  const theme = useTheme();

  return (
    <Box
      data-testid="map-view"
      sx={{
        backgroundColor: theme.palette.grey.xxLight,
        color: theme.palette.common.black,
        marginBottom: variant === MapVariant.Fullscreen ? 0 : '1rem',
        position: 'relative',
      }}
    >
      {children}
    </Box>
  );
};

const Canvas: FC<PropsWithChildren<{ variant?: MapVariant; mapRef?: RefObject<HTMLDivElement> }>> = ({
  children,
  mapRef,
  variant,
}) => {
  const theme = useTheme();

  return (
    <Box
      data-testid="map-canvas"
      ref={mapRef}
      sx={{
        '& .gcd-txt-result': {
          '& a': {
            '&:hover': {
              background: 'white',
            },
            display: 'block',
          },
          background: '#ffffffcf',
          display: 'inline-block',
          padding: '0.5em',
        },
        '& .ol-compass': {
          display: 'inline-block',
          zIndex: 10,
        },
        '& .ol-control': {
          '& button': {
            '&:hover, &:focus': {
              backgroundColor: theme.palette.grey.xLight,
            },
            alignSelf: 'flex-start',
            backgroundColor: theme.palette.grey.xxLight,
            border: `solid 1px ${theme.palette.grey.xLight}`,
            borderRadius: theme.shape.borderRadius,
            color: theme.palette.grey.midDark,
            cursor: 'pointer',
            display: 'inline-block',
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.fontSize,
            height: '3em',
            lineHeight: 'unset',
            marginRight: '0.1em',
            width: '3em',
          },
          '&.current-location': {
            left: 'auto',
            right: 2,
          },
          '&.interaction-type': {
            '&-1': {
              left: 46,
            },
            '&-2': {
              left: 90,
            },
            top: 0,
          },
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0)',
          },
          backgroundColor: 'rgba(0,0,0,0)',
          display: 'inline-block',
          left: 2,
          top: 'unset',
          zIndex: 10,
          // eslint-disable-next-line @typescript-eslint/no-magic-numbers
          bottom: variant === MapVariant.Fullscreen ? 0 : 2,
          position: variant === MapVariant.Fullscreen ? 'fixed!important' : 'absolute',
        },
        '& canvas': {
          borderRadius: theme.shape.borderRadius,
        },
        height: variant === MapVariant.Fullscreen ? `calc(100vh - ${theme.appBar.height})` : '16rem',
        maxHeight: variant === MapVariant.Fullscreen ? 'unset' : '70vh',
        position: 'relative',
        zIndex: 9,
      }}
    >
      {children}
    </Box>
  );
};

const MapCanvas = (props: MapCanvasProps): JSX.Element => {
  const theme = useTheme();
  const { navigate, overlay, overlayPosition, variant = MapVariant.Default } = props;

  const { error, map, mapRef, mapToken, requestMapToken } = useMap(props);

  useEffect(() => {
    if (map && props.setMap) {
      props.setMap(map);
    }
  }, [props.setMap, map]);

  const overlayRef = useOverlay({
    map,
    navigate,
    overlay,
    overlayPosition,
  });

  if (mapToken.loading) {
    return <LoadingCardContent />;
  }

  const currentError = error || mapToken.error;

  if (!mapRef || currentError) {
    return (
      <Wrapper variant={variant}>
        <Canvas variant={variant} />
        <Box
          sx={{
            '& .fa': {
              fontSize: '2em',
            },
            alignItems: 'center',
            gap: '1rem',
            color: theme.palette.grey.main,
            display: 'flex',
            flexDirection: 'column',
            left: 0,
            margin: 'auto',
            position: 'absolute',
            right: 0,
            top: '50%',
            zIndex: 9,
          }}
        >
          <FontAwesome name="map-o" />
          {currentError && <ErrorButtonWithFeedback variant="text" onReload={requestMapToken} />}
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper variant={variant}>
      <Canvas mapRef={mapRef} variant={variant} />
      <OverlayContainer overlayRef={overlayRef.current}>{overlay}</OverlayContainer>
    </Wrapper>
  );
};

export default MapCanvas;
